import { serviceRoleClient } from './serviceRoleClient';
import { ENV, SUPABASE_URL } from '../config/env';
import {
  insertBulkRecipients,
  resumeBulkCampaign as resumeBulkCampaignProcessor,
  runBulkCampaignProcessor,
} from './bulkCampaignProcessor';
import { marketingAdminAuthHeader, marketingGenerateWaTemplateUrl } from './marketingAdminService';

const fnHeaders = {
  'Content-Type': 'application/json',
  apikey: ENV.SUPABASE_ANON_KEY,
  Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
};

const toError = (err, fallback = 'Request failed') => {
  if (err instanceof Error) return err;
  const msg = err?.message || err?.error_description || err?.details || fallback;
  return new Error(typeof msg === 'string' ? msg : fallback);
};

const isMissingRelation = (error) => {
  const msg = String(error?.message || '');
  return error?.code === '42P01' || error?.code === 'PGRST202' || msg.includes('does not exist') || msg.includes('Could not find');
};

const isStatementTimeout = (error) => {
  const msg = String(error?.message || '');
  return error?.code === '57014' || msg.includes('statement timeout') || msg.includes('canceling statement');
};

const formatMessagingLimitTier = (tier) => {
  if (tier == null || tier === '') return '—';
  const key = String(tier).trim().toUpperCase();
  const map = {
    TIER_50: '50 users/day',
    TIER_250: '250 users/day',
    TIER_1K: '1K users/day',
    TIER_2K: '2K users/day',
    TIER_10K: '10K users/day',
    TIER_100K: '100K users/day',
    TIER_UNLIMITED: 'Unlimited',
    UNLIMITED: 'Unlimited',
    '2000': '2K users/day',
    '10000': '10K users/day',
    '100000': '100K users/day',
  };
  if (map[key]) return map[key];
  if (/^\d+$/.test(key)) return `${Number(key).toLocaleString('en-IN')} users/day`;
  return String(tier);
};

const pickMessagingLimit = (phoneJson = {}, wabaJson = {}) =>
  phoneJson.whatsapp_business_manager_messaging_limit
  || wabaJson.whatsapp_business_manager_messaging_limit
  || phoneJson.messaging_limit_tier
  || wabaJson.messaging_limit_tier
  || null;

const normalizeAccountDetails = (phoneJson = {}, wabaJson = {}) => {
  const messagingLimit = pickMessagingLimit(phoneJson, wabaJson);
  return {
    verified_name: phoneJson.verified_name || null,
    display_phone_number: phoneJson.display_phone_number || null,
    status: phoneJson.status || 'UNKNOWN',
    quality_rating: phoneJson.quality_rating || 'UNKNOWN',
    messaging_limit_tier: messagingLimit,
    messaging_limit_label: formatMessagingLimitTier(messagingLimit),
    two_fa_enabled: Boolean(
      phoneJson.is_pin_enabled
      || wabaJson.two_step_verification_enabled
      || phoneJson.two_step_verification_enabled,
    ),
    is_official_business_account: Boolean(phoneJson.is_official_business_account),
    fetched_at: new Date().toISOString(),
  };
};

export const EMPTY_STATS = {
  accounts_total: 0,
  accounts_default: 0,
  rules_total: 0,
  rules_enabled: 0,
  ai_enabled: false,
  messages_today: 0,
  messages_total: 0,
  outbound_total: 0,
  inbound_total: 0,
  by_source: {},
  by_status: {},
  by_account: [],
  automation_log_total: 0,
  automation_sent: 0,
  automation_failed: 0,
  bulk_campaigns_total: 0,
  bulk_sent_total: 0,
  customer_segments: {
    all: 0,
    active_membership: 0,
    pending_membership: 0,
    expiring_soon: 0,
    expiring_60_days: 0,
    expiring_90_days: 0,
    expiring_180_days: 0,
    expiring_365_days: 0,
    expired_membership: 0,
    app_installed: 0,
    no_app: 0,
    no_membership: 0,
    has_vehicle: 0,
    no_vehicle: 0,
    whatsapp_contacted: 0,
    ever_responded: 0,
    never_responded: 0,
  },
  daily_outbound: [],
  recent_automation_logs: [],
  recent_bulk_campaigns: [],
  _setup_required: false,
};

export const WhatsAppAutomationService = {
  async listAccounts() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_accounts_safe')
      .select('*')
      .eq('owner_type', 'company')
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load accounts');
    }
    return data || [];
  },

  async saveAccount(payload) {
    const row = {
      name: payload.name,
      phone_number_id: payload.phone_number_id,
      waba_id: payload.waba_id,
      permanent_token_encrypted: payload.permanent_token,
      verify_token: payload.verify_token || null,
      display_phone: payload.display_phone || null,
      owner_type: 'company',
      owner_user_id: null,
      is_default: !!payload.is_default,
      is_active: payload.is_active !== false,
      ai_enabled: !!payload.ai_enabled,
      updated_at: new Date().toISOString(),
    };

    if (payload.id) {
      if (!payload.permanent_token) delete row.permanent_token_encrypted;
      const { data, error } = await serviceRoleClient
        .from('whatsapp_accounts')
        .update(row)
        .eq('id', payload.id)
        .select('id')
        .single();
      if (error) throw toError(error, 'Failed to save account');
      return data;
    }

    const { data, error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .insert(row)
      .select('id')
      .single();
    if (error) throw toError(error, 'Failed to save account');
    return data;
  },

  async deleteAccount(accountId) {
    if (!accountId) throw new Error('Account ID required');
    const { error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .delete()
      .eq('id', accountId);
    if (error) throw toError(error, 'Failed to delete account');
    return { success: true };
  },

  async getAccountDetails(accountId) {
    const { data: acc, error } = await serviceRoleClient
      .from('whatsapp_accounts')
      .select('phone_number_id, waba_id, permanent_token_encrypted')
      .eq('id', accountId)
      .single();
    if (error || !acc) throw toError(error, 'Account not found');

    const phoneFields = [
      'display_phone_number',
      'verified_name',
      'quality_rating',
      'status',
      'whatsapp_business_manager_messaging_limit',
      'messaging_limit_tier',
      'is_official_business_account',
      'is_pin_enabled',
    ].join(',');

    const [phoneRes, wabaRes] = await Promise.all([
      fetch(
        `https://graph.facebook.com/v21.0/${acc.phone_number_id}?fields=${phoneFields}`,
        { headers: { Authorization: `Bearer ${acc.permanent_token_encrypted}` } },
      ),
      acc.waba_id
        ? fetch(
          `https://graph.facebook.com/v21.0/${acc.waba_id}?fields=two_step_verification_enabled,whatsapp_business_manager_messaging_limit,messaging_limit_tier`,
          { headers: { Authorization: `Bearer ${acc.permanent_token_encrypted}` } },
        )
        : Promise.resolve(null),
    ]);

    const phoneJson = await phoneRes.json();
    if (!phoneRes.ok) throw new Error(phoneJson?.error?.message || 'Meta phone API failed');

    let wabaJson = {};
    if (wabaRes) {
      wabaJson = await wabaRes.json();
      if (!wabaRes.ok) wabaJson = {};
    }

    return normalizeAccountDetails(phoneJson, wabaJson);
  },

  async testAccount(accountId) {
    const details = await WhatsAppAutomationService.getAccountDetails(accountId);
    return details;
  },

  async getCustomerSegmentStats() {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_customer_segment_stats');
    if (error) {
      if (isMissingRelation(error)) return null;
      throw toError(error, 'Failed to load customer segments');
    }
    return data && typeof data === 'object' ? data : null;
  },

  async getStats() {
    const [statsRes, segmentsRes] = await Promise.allSettled([
      serviceRoleClient.rpc('whatsapp_automation_stats', { p_days: 0 }),
      serviceRoleClient.rpc('whatsapp_customer_segment_stats'),
    ]);

    if (statsRes.status === 'rejected') {
      throw toError(statsRes.reason, 'Failed to load stats');
    }

    const { data, error } = statsRes.value;
    if (error) {
      if (isMissingRelation(error)) {
        return { ...EMPTY_STATS, _setup_required: true };
      }
      throw toError(error, 'Failed to load stats');
    }

    const stats = data || {};
    let segments = stats.customer_segments && typeof stats.customer_segments === 'object'
      ? stats.customer_segments
      : {};

    let audienceCache = null;
    if (segmentsRes.status === 'fulfilled' && !segmentsRes.value.error) {
      const segData = segmentsRes.value.data;
      if (segData && typeof segData === 'object') {
        const { cache, ...counts } = segData;
        segments = counts;
        audienceCache = cache || null;
      }
    }

    return {
      ...EMPTY_STATS,
      ...stats,
      outbound_total: stats.outbound_total ?? stats.outbound_period ?? 0,
      inbound_total: stats.inbound_total ?? stats.inbound_period ?? 0,
      bulk_sent_total: stats.bulk_sent_total ?? stats.bulk_sent_period ?? 0,
      bulk_campaigns_total: stats.bulk_campaigns_total ?? stats.bulk_campaigns_period ?? 0,
      automation_log_total: stats.automation_log_total ?? stats.automation_log_period ?? 0,
      by_source: stats.by_source || {},
      by_status: stats.by_status || {},
      by_account: Array.isArray(stats.by_account) ? stats.by_account : [],
      customer_segments: { ...EMPTY_STATS.customer_segments, ...segments },
      audience_cache: audienceCache,
      recent_automation_logs: Array.isArray(stats.recent_automation_logs) ? stats.recent_automation_logs : [],
      recent_bulk_campaigns: Array.isArray(stats.recent_bulk_campaigns) ? stats.recent_bulk_campaigns : [],
      _segments_pending: segmentsRes.status === 'rejected'
        || (segmentsRes.status === 'fulfilled' && !!segmentsRes.value.error && isStatementTimeout(segmentsRes.value.error)),
    };
  },

  async listRules() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load rules');
    }
    return data || [];
  },

  async saveRule(payload) {
    const row = {
      name: payload.name,
      is_enabled: payload.is_enabled !== false,
      trigger_type: payload.trigger_type,
      trigger_value: payload.trigger_value,
      sender_mode: payload.sender_mode || 'company_default',
      account_id: payload.sender_mode === 'specific_account' ? payload.account_id : null,
      message_mode: payload.message_mode || 'text',
      template_name: payload.template_name || null,
      template_language: payload.template_language || 'en',
      template_params: payload.template_params || [],
      text_body: payload.text_body || null,
      delay_minutes: Number(payload.delay_minutes || 0),
      only_if_unassigned: !!payload.only_if_unassigned,
      enable_ai_reply: !!payload.enable_ai_reply,
      updated_at: new Date().toISOString(),
    };

    if (payload.id) {
      const { data, error } = await serviceRoleClient
        .from('whatsapp_automation_rules')
        .update(row)
        .eq('id', payload.id)
        .select('id')
        .single();
      if (error) throw toError(error, 'Failed to save rule');
      return data;
    }

    const { data, error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .insert(row)
      .select('id')
      .single();
    if (error) throw toError(error, 'Failed to save rule');
    return data;
  },

  async toggleRule(id, enabled) {
    const { error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .update({ is_enabled: enabled, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw toError(error, 'Failed to toggle rule');
  },

  async deleteRule(id) {
    const { error } = await serviceRoleClient
      .from('whatsapp_automation_rules')
      .delete()
      .eq('id', id);
    if (error) throw toError(error, 'Failed to delete rule');
  },

  async listLogs(limit = 50) {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_automation_log')
      .select('*, whatsapp_automation_rules(name)')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load logs');
    }
    return data || [];
  },

  async syncTemplates(accountId) {
    const url = accountId
      ? `${SUPABASE_URL}/functions/v1/whatsapp-templates?account_id=${accountId}`
      : `${SUPABASE_URL}/functions/v1/whatsapp-templates`;
    const res = await fetch(url, { headers: fnHeaders });
    const json = await res.json().catch(() => ({}));
    if (res.status === 401) {
      throw new Error(json?.message || json?.error || 'Unauthorized — template sync failed');
    }
    return json;
  },

  async createTemplate(payload) {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-template-create`, {
      method: 'POST',
      headers: fnHeaders,
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (res.status === 401) {
      throw new Error(json?.message || json?.error || 'Unauthorized — edge function JWT check failed');
    }
    return json;
  },

  async sendBulk(payload) {
    const segmentQueue = !!payload.segment_queue;

    let total = Number(payload.expected_total || 0);
    if (segmentQueue && !total) {
      const page = await WhatsAppAutomationService.listBulkCustomers({
        segment: payload.segment || 'all',
        search: payload.search || '',
        state: payload.state_filter || '',
        limit: 1,
        offset: 0,
      });
      total = Number(page.total || 0);
    } else if (!segmentQueue) {
      total = Array.isArray(payload.recipients) ? payload.recipients.length : 0;
    }

    if (!payload.name?.trim() || total <= 0) {
      return { success: false, error: 'name and recipients required' };
    }

    const templateParamsStored = payload.message_mode === 'template'
      ? {
          body: Array.isArray(payload.template_params) ? payload.template_params : [],
          header_type: payload.template_header_type || 'NONE',
          header_image_url: payload.template_header_image_url || '',
        }
      : (payload.template_params || []);

    const { data: campaign, error: campErr } = await serviceRoleClient
      .from('whatsapp_bulk_campaigns')
      .insert({
        name: payload.name.trim(),
        account_id: payload.account_id || null,
        message_mode: payload.message_mode || 'text',
        template_name: payload.template_name || null,
        template_language: payload.template_language || 'en',
        template_params: templateParamsStored,
        text_body: payload.text_body || null,
        total_recipients: total,
        segment: payload.segment || null,
        search: payload.search || null,
        state_filter: payload.state_filter || null,
        track_links: !!payload.track_links,
        parent_campaign_id: payload.parent_campaign_id || null,
        status: 'pending',
      })
      .select('id')
      .single();

    if (campErr || !campaign) {
      throw toError(campErr, 'Failed to create campaign');
    }

    if (!segmentQueue && payload.recipients?.length) {
      void insertBulkRecipients(campaign.id, payload.recipients, {
        messageMode: payload.message_mode,
        textBody: payload.text_body,
        trackLinks: payload.track_links,
      }).then(() => {
        void runBulkCampaignProcessor(campaign.id);
      }).catch(async (err) => {
        console.error('[sendBulk] recipient insert failed:', err);
        await serviceRoleClient
          .from('whatsapp_bulk_campaigns')
          .update({ status: 'failed', completed_at: new Date().toISOString() })
          .eq('id', campaign.id);
      });
    } else {
      void runBulkCampaignProcessor(campaign.id);
    }

    return {
      success: true,
      async: true,
      campaign_id: campaign.id,
      total,
      status: 'queued',
      message: 'Campaign queued — sending continues in background',
    };
  },

  async resumeBulkCampaign(campaignId) {
    return resumeBulkCampaignProcessor(campaignId);
  },

  async cancelBulkCampaign(campaignId) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_cancel_campaign', {
      p_campaign_id: campaignId,
    });
    if (error) throw toError(error, 'Failed to cancel campaign');
    if (data?.success === false) throw new Error(data.error || 'Cancel failed');
    return data;
  },

  async listBulkCustomers({ segment = 'all', search = '', state = '', limit = 100, offset = 0 } = {}) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_customers', {
      p_segment: segment,
      p_search: search,
      p_limit: limit,
      p_offset: offset,
      p_state: state,
    });
    if (error) {
      if (isMissingRelation(error)) {
        return { items: [], total: 0, _setup_required: true };
      }
      throw toError(error, 'Failed to load customers');
    }
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      items: items.map((row) => ({
        ...row,
        app_installed: !!row.app_installed,
        variables: {
          customer_name: row.customer_name || 'Customer',
          phone: row.phone || '',
          membership_number: row.membership_number || '',
          plan_name: row.plan_name || '',
          start_date: row.start_date || '',
          expiry_date: row.expiry_date || '',
          membership_status: row.membership_status || '',
          app_installed: row.app_installed ? 'Yes' : 'No',
          car: row.car || '',
          car_model: row.car_model || '',
          state: row.state || '',
          pincode: row.pincode || '',
          email: row.email || '',
        },
      })),
      total: Number(data?.total ?? items.length),
      offset: Number(data?.offset || offset),
      limit: Number(data?.limit || limit),
      cache: data?.cache || null,
    };
  },

  /** Preview only — never pull 1L rows into the browser. Send uses segment_queue. */
  async listAllBulkCustomers(filters = {}) {
    return WhatsAppAutomationService.listBulkCustomers({
      ...filters,
      limit: 100,
      offset: 0,
    });
  },

  async getBulkAudienceStatus() {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_audience_status');
    if (error) {
      if (isMissingRelation(error)) return { empty: true, _setup_required: true };
      throw toError(error, 'Failed to load audience cache status');
    }
    return data || { empty: true };
  },

  async refreshBulkAudience() {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_refresh_audience');
    if (error) {
      if (isMissingRelation(error)) {
        return {
          success: false,
          _setup_required: true,
          error: 'Run whatsapp-bulk-audience-cache.sql in Supabase first',
        };
      }
      throw toError(error, 'Audience cache refresh failed (run SQL in Supabase if gateway times out)');
    }
    return data || { success: true };
  },

  async listBulkCampaigns(limit = 50) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_list_campaigns', { p_limit: limit });
    if (error) {
      if (isMissingRelation(error)) return [];
      throw toError(error, 'Failed to load campaigns');
    }
    return Array.isArray(data) ? data : [];
  },

  /** Pull Meta Template Analytics URL button clicks (not custom short-link tracker). */
  async syncBulkCampaignMetaUrlClicks(campaignIds = []) {
    const ids = (Array.isArray(campaignIds) ? campaignIds : []).filter(Boolean).slice(0, 20);
    if (!ids.length) return { success: true, results: [] };
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-campaign-meta-clicks`, {
      method: 'POST',
      headers: fnHeaders,
      body: JSON.stringify({ campaign_ids: ids }),
    });
    const json = await res.json().catch(() => ({}));
    if (!json?.success && json?.error) {
      throw toError(json, 'Failed to sync Meta URL clicks');
    }
    return json;
  },

  async getBulkCampaignDetail(campaignId) {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_campaign_detail', {
      p_campaign_id: campaignId,
    });
    if (error) throw toError(error, 'Failed to load campaign detail');
    return data || {};
  },

  async getRetargetRecipients(campaignId, filter = 'no_response') {
    const { data, error } = await serviceRoleClient.rpc('whatsapp_bulk_retarget_recipients', {
      p_campaign_id: campaignId,
      p_filter: filter,
    });
    if (error) throw toError(error, 'Failed to load retarget list');
    const items = Array.isArray(data?.items) ? data.items : [];
    return {
      filter: data?.filter || filter,
      total: Number(data?.total || items.length),
      items: items.map((row) => ({
        ...row,
        phone: row.phone,
        customer_name: row.customer_name || row.variables?.customer_name || 'Customer',
        variables: row.variables || {
          customer_name: row.customer_name || 'Customer',
          phone: row.phone || '',
          car: row.car || '',
          car_model: row.car_model || '',
          state: row.state || '',
          pincode: row.pincode || '',
          membership_status: row.membership_status || '',
          expiry_date: row.expiry_date || '',
        },
      })),
    };
  },

  async getAiMemory() {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_ai_memory')
      .select('*')
      .eq('id', 1)
      .maybeSingle();
    if (error) {
      if (isMissingRelation(error)) {
        return { system_prompt: '', global_enabled: false, escalation_keywords: [] };
      }
      throw toError(error, 'Failed to load AI memory');
    }
    return data;
  },

  async saveAiMemory(payload) {
    const { data, error } = await serviceRoleClient
      .from('whatsapp_ai_memory')
      .upsert({
        id: 1,
        system_prompt: payload.system_prompt,
        model: payload.model || 'gpt-4o-mini',
        max_tokens: Number(payload.max_tokens || 300),
        temperature: Number(payload.temperature || 0.7),
        global_enabled: !!payload.global_enabled,
        business_hours: payload.business_hours || {},
        escalation_keywords: payload.escalation_keywords || [],
        fallback_template_name: payload.fallback_template_name || 'service_booking_update',
        fallback_template_language: payload.fallback_template_language || 'en',
        updated_at: new Date().toISOString(),
      })
      .select('*')
      .single();
    if (error) throw toError(error, 'Failed to save AI memory');
    return data;
  },

  async testAiReply(message, phone) {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-ai-reply`, {
      method: 'POST',
      headers: fnHeaders,
      body: JSON.stringify({
        contact_phone: phone || '919999999999',
        contact_name: 'Test',
        message_content: message,
        dry_run: true,
      }),
    });
    return res.json();
  },

  getWebhookUrl() {
    return `${SUPABASE_URL}/functions/v1/whatsapp-webhook`;
  },

  /** Read legacy WHATSAPP_* edge secrets via edge function (verify_jwt must be off). */
  async getSupabaseEnvCredentials() {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-env-import`, {
      method: 'POST',
      headers: {
        ...fnHeaders,
        Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ action: 'get' }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json?.success) {
      throw new Error(json?.error || `Import failed (${res.status})`);
    }
    return json.credentials;
  },

  /** Generate Meta-compliant WhatsApp template draft (marketing-site API — same as blog generate). */
  async generateTemplateWithAi({ category, language, brief, templateName = '' }) {
    if (!brief?.trim()) throw new Error('Describe what this template is about');

    const res = await fetch(marketingGenerateWaTemplateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...marketingAdminAuthHeader(),
      },
      body: JSON.stringify({
        category: category || 'UTILITY',
        language: language || 'en',
        brief: brief.trim(),
        template_name: templateName || '',
      }),
    });

    const json = await res.json().catch(() => ({}));
    if (res.status === 401) {
      throw new Error(json?.error || 'Unauthorized — check CRON_SECRET matches marketing-site');
    }
    if (!json?.success || !json?.draft) {
      throw new Error(json?.error || `AI generation failed (${res.status}) — is marketing-site running on :3010?`);
    }
    return json.draft;
  },
};
